import { useState, useCallback, useMemo } from "react";
import {
  useGetPortalCartQuery,
  useAddPortalCartItemMutation,
  useUpdatePortalCartItemMutation,
  useRemovePortalCartItemMutation,
  useClearPortalCartMutation,
  useSubmitCheckoutMutation,
} from "../../../store/apiSlice/portalCartApiSlice";

export const usePortalCart = () => {
  const { data: cart, isLoading } = useGetPortalCartQuery();
  const [addPortalCartItem, { isLoading: isAdding }] = useAddPortalCartItemMutation();
  const [updatePortalCartItem] = useUpdatePortalCartItemMutation();
  const [removePortalCartItem] = useRemovePortalCartItemMutation();
  const [clearPortalCart, { isLoading: isClearing }] = useClearPortalCartMutation();
  const [submitCheckout, { isLoading: isCheckingOut }] = useSubmitCheckoutMutation();

  const [addingProductId, setAddingProductId] = useState(null);
  const [updatingItemId, setUpdatingItemId] = useState(null);
  const [removingItemId, setRemovingItemId] = useState(null);

  const addItem = useCallback(
    async (productId, quantity) => {
      setAddingProductId(productId);
      try {
        const result = await addPortalCartItem({ product_id: productId, quantity }).unwrap();
        return { success: true, data: result };
      } catch (err) {
        const errorMsg =
          err?.data?.detail ||
          err?.data?.message ||
          "Failed to add item to cart. Please check stock availability.";
        return { success: false, error: errorMsg };
      } finally {
        setAddingProductId(null);
      }
    },
    [addPortalCartItem],
  );

  const itemCount = useMemo(() => {
    let count = 0;
    cart?.items?.forEach(item => {
      count += Number(item.quantity);
    });
    return count;
  }, [cart?.items]);

  const updateItem = useCallback(
    async (itemId, quantity) => {
      setUpdatingItemId(itemId);
      try {
        const result = await updatePortalCartItem({ id: itemId, quantity }).unwrap();
        return { success: true, data: result };
      } catch (err) {
        const errorMsg =
          err?.data?.detail || err?.data?.message || "Failed to update item quantity.";
        return { success: false, error: errorMsg };
      } finally {
        setUpdatingItemId(null);
      }
    },
    [updatePortalCartItem],
  );

  const removeItem = useCallback(
    async itemId => {
      setRemovingItemId(itemId);
      try {
        const result = await removePortalCartItem(itemId).unwrap();
        return { success: true, data: result };
      } catch (err) {
        const errorMsg =
          err?.data?.detail || err?.data?.message || "Failed to remove item from cart.";
        return { success: false, error: errorMsg };
      } finally {
        setRemovingItemId(null);
      }
    },
    [removePortalCartItem],
  );

  const clearCart = useCallback(async () => {
    try {
      const result = await clearPortalCart().unwrap();
      return { success: true, data: result };
    } catch (err) {
      const errorMsg = err?.data?.detail || err?.data?.message || "Failed to clear cart.";
      return { success: false, error: errorMsg };
    }
  }, [clearPortalCart]);

  const checkout = useCallback(
    async ({
      currency_id,
      shipping_address_id,
      po_number,
      requested_delivery_date,
      special_instructions,
    }) => {
      try {
        const result = await submitCheckout({
          currency_id,
          shipping_address_id,
          po_number,
          requested_delivery_date,
          special_instructions,
        }).unwrap();
        return {
          success: true,
          saleOrderId: result.sale_order_id,
          salesNo: result.sales_no,
          message: result.message,
        };
      } catch (err) {
        let errorMsg = "Checkout failed. Please review your order details.";
        if (err?.data) {
          if (typeof err.data === "string") {
            errorMsg = err.data;
          } else if (err.data.detail) {
            errorMsg = err.data.detail;
          } else if (err.data.message) {
            errorMsg = err.data.message;
          } else {
            // Normalize serializer errors
            const messages = [];
            Object.values(err.data).forEach(val => {
              if (Array.isArray(val)) {
                messages.push(...val);
              } else if (typeof val === "string") {
                messages.push(val);
              }
            });
            if (messages.length > 0) {
              errorMsg = messages.join(" ");
            }
          }
        }
        return { success: false, error: errorMsg, errorData: err.data };
      }
    },
    [submitCheckout],
  );

  const items = cart?.items || [];
  const subtotal = cart?.subtotal || 0;
  const taxTotal = cart?.tax_total || 0;
  const total = cart?.total || 0;

  return {
    cart,
    items,
    itemCount,
    subtotal,
    taxTotal,
    total,
    isLoading,
    addItem,
    updateItem,
    removeItem,
    clearCart,
    checkout,
    isAddingItem: isAdding,
    addingProductId,
    updatingItemId,
    removingItemId,
    isClearing,
    isCheckingOut,
  };
};
